import { Box } from '@mui/material';
import useRawMaterials from 'FeatureModules/ComponentChain/hooks/useRawMaterials';
import Loader from 'Components/Forms/Loader';
import { FieldWrapper } from 'DesignSystem';
import AutocompleteSelection from 'DesignSystem/Inputs/AutocompleteSelection/AutocompleteSelection';
import { useField } from 'formik';
import React, { FC } from 'react';

interface IRawMaterialSelectionProps {
  name: string;
  size?: 'small' | 'medium';
  helperText?: string;
  isRequired?: boolean;
  multiple?: boolean; // Default is true
  label?: string;

  /**
   * The whole list of aw materials will be filtered to only allow selection of these raw material ids
   */
  selectableRawMaterialIds?: string[];

  labelVariant?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  enableAddNewItem?: boolean;
  'data-testid'?: string;
}

const RawMaterialSelection: FC<IRawMaterialSelectionProps> = ({
  name,
  size = 'medium',
  helperText,
  isRequired,
  multiple = true,
  selectableRawMaterialIds,
  enableAddNewItem = false,
  label = 'Raw material',
  labelVariant,
  disabled,
  'data-testid': testId,
}) => {
  const { rawMaterials, loading, createRawMaterial } = useRawMaterials();

  const [{ value }, { touched, error }, { setValue, setTouched }] = useField<
    string[] | string | undefined
  >(name);

  const selectableRawMaterials = selectableRawMaterialIds
    ? rawMaterials.filter(({ id }) => selectableRawMaterialIds.includes(id))
    : rawMaterials;

  const handleSelectionChange = async (selection: string[] | string | undefined) => {
    await setTouched(true);
    setValue(selection || undefined, true);
  };

  const handleAddNewItemClick = (title: string) => {
    createRawMaterial({ variables: { title } });
  };

  return (
    <FieldWrapper
      label={`${label}${multiple ? 's' : ''}`}
      labelIcon="raw-material"
      variant={labelVariant || size}
      required={isRequired}
      tooltip={
        helperText
          ? {
              helperText,
              variant: 'INFO',
            }
          : undefined
      }
    >
      {loading ? (
        <Box position="relative">
          <Loader size={16} />
        </Box>
      ) : (
        <AutocompleteSelection
          label="Select raw material"
          disabled={disabled}
          closeAfterSelection
          enableAddNewItem={enableAddNewItem}
          multiple={multiple}
          data-testid={testId}
          size={size}
          itemName="raw material"
          items={selectableRawMaterials}
          errorMessage={error}
          hasError={!!error && touched}
          selected={value || (multiple ? [] : '')}
          onAddNewItemClick={handleAddNewItemClick}
          onSelectionChange={handleSelectionChange}
        />
      )}
    </FieldWrapper>
  );
};

export default RawMaterialSelection;
